import {invalidateQueries, q} from "../../../state";
import {
  ComplexityAggregationOperator,
  ComplexityModelId,
  ComplexityModelVersion,
  ComplexityModelVersionId,
  CreateComplexityModelVersion,
  DuplicateComplexityModelVersion,
  UpdateComplexityModelVersionName,
} from "platformed-client/types";
import {platformedClient} from "../..";

/**
 * Create a new version for a complexity model
 */
export async function create(
  modelId: ComplexityModelId,
  version: CreateComplexityModelVersion,
): Promise<ComplexityModelVersionId> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/complexity/{model_id}/version",
    {path: {model_id: modelId}, body: version},
  );
  await invalidateQueries([
    q.tprm.complexityModels.filter(),
    q.tprm.complexityModel.filter(modelId),
    q.tprm.complexityModelVersions.filter(modelId),
  ]);
  return res;
}

/**
 * Duplicate an existing version of a complexity model
 */
export async function duplicate(
  modelId: ComplexityModelId,
  duplicate: DuplicateComplexityModelVersion,
): Promise<ComplexityModelVersionId> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/complexity/{model_id}/version/duplicate",
    {path: {model_id: modelId}, body: duplicate},
  );
  await invalidateQueries([
    q.tprm.complexityModels.filter(),
    q.tprm.complexityModel.filter(modelId),
    q.tprm.complexityModelVersions.filter(modelId),
  ]);
  return res;
}

/**
 * List all versions of a complexity model
 */
export async function list(modelId: ComplexityModelId): Promise<ComplexityModelVersion[]> {
  return await platformedClient.internalRequest("get", "/region/{region}/api/tprm/complexity/{model_id}/versions", {
    path: {model_id: modelId},
  });
}

/**
 * Get a specific version of a complexity model
 */
export async function get_(
  modelId: ComplexityModelId,
  versionId: ComplexityModelVersionId,
): Promise<ComplexityModelVersion> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/complexity/{model_id}/version/{version_id}",
    {path: {model_id: modelId, version_id: versionId}},
  );
}

/**
 * Update the name of a complexity model version
 */
export async function updateName(
  modelId: ComplexityModelId,
  versionId: ComplexityModelVersionId,
  update: UpdateComplexityModelVersionName,
) {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/complexity/{model_id}/version/{version_id}/name",
    {path: {model_id: modelId, version_id: versionId}, body: update},
  );
  await invalidateQueries([
    q.tprm.complexityModels.filter(),
    q.tprm.complexityModel.filter(modelId),
    q.tprm.complexityModelVersions.filter(modelId),
    q.tprm.complexityModelVersion.filter(modelId, versionId),
    q.tprm.complexityModelVersionExport.filter(modelId, versionId),
  ]);
  return res;
}

/**
 * Update the aggregation operator of a complexity model version
 */
export async function updateOperator(
  modelId: ComplexityModelId,
  versionId: ComplexityModelVersionId,
  operator: ComplexityAggregationOperator,
) {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/complexity/{model_id}/version/{version_id}/operator",
    {path: {model_id: modelId, version_id: versionId}, body: operator},
  );
  await invalidateQueries([
    q.tprm.complexityModels.filter(),
    q.tprm.complexityModel.filter(modelId),
    q.tprm.complexityModelVersions.filter(modelId),
    q.tprm.complexityModelVersion.filter(modelId, versionId),
    q.tprm.complexityModelVersionExport.filter(modelId, versionId),
  ]);
  return res;
}

/**
 * Publish a complexity model version (makes it immutable)
 */
export async function publish(modelId: ComplexityModelId, versionId: ComplexityModelVersionId) {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/complexity/{model_id}/version/{version_id}/publish",
    {path: {model_id: modelId, version_id: versionId}},
  );
  await invalidateQueries([
    q.tprm.complexityModels.filter(),
    q.tprm.complexityModel.filter(modelId),
    q.tprm.complexityModelVersions.filter(modelId),
    q.tprm.complexityModelVersion.filter(modelId, versionId),
    q.tprm.complexityModelVersionExport.filter(modelId, versionId),
  ]);
  return res;
}

/**
 * Archive a complexity model version (prevents use in new assessments)
 */
export async function archive(modelId: ComplexityModelId, versionId: ComplexityModelVersionId) {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/complexity/{model_id}/version/{version_id}/archive",
    {path: {model_id: modelId, version_id: versionId}},
  );
  await invalidateQueries([
    q.tprm.complexityModels.filter(),
    q.tprm.complexityModel.filter(modelId),
    q.tprm.complexityModelVersions.filter(modelId),
    q.tprm.complexityModelVersion.filter(modelId, versionId),
    q.tprm.complexityModelVersionExport.filter(modelId, versionId),
  ]);
  return res;
}

/**
 * Unarchive a complexity model version (makes it available for new assessments)
 */
export async function unarchive(modelId: ComplexityModelId, versionId: ComplexityModelVersionId) {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/complexity/{model_id}/version/{version_id}/unarchive",
    {path: {model_id: modelId, version_id: versionId}},
  );
  await invalidateQueries([
    q.tprm.complexityModels.filter(),
    q.tprm.complexityModel.filter(modelId),
    q.tprm.complexityModelVersions.filter(modelId),
    q.tprm.complexityModelVersion.filter(modelId, versionId),
    q.tprm.complexityModelVersionExport.filter(modelId, versionId),
  ]);
  return res;
}

/**
 * Delete a complexity model version
 */
export async function delete_(modelId: ComplexityModelId, versionId: ComplexityModelVersionId) {
  const res = await platformedClient.internalRequest(
    "delete",
    "/region/{region}/api/tprm/complexity/{model_id}/version/{version_id}",
    {path: {model_id: modelId, version_id: versionId}},
  );
  await invalidateQueries([
    q.tprm.complexityModels.filter(),
    q.tprm.complexityModel.filter(modelId),
    q.tprm.complexityModelVersions.filter(modelId),
    q.tprm.complexityModelVersion.filter(modelId, versionId),
    q.tprm.complexityModelVersionExport.filter(modelId, versionId),
  ]);
  return res;
}

/**
 * Export a complexity model version as a preset configuration
 */
export async function exportAsPreset(
  modelId: ComplexityModelId,
  versionId: ComplexityModelVersionId,
): Promise<CreateComplexityModelVersion> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/complexity/{model_id}/version/{version_id}/export",
    {path: {model_id: modelId, version_id: versionId}},
  );
}

/**
 * Create a complexity model version from a preset configuration
 */
export async function createFromPreset(
  modelId: ComplexityModelId,
  version: CreateComplexityModelVersion,
): Promise<ComplexityModelVersionId> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/complexity/{model_id}/version/create_from_preset",
    {path: {model_id: modelId}, body: version},
  );
  await invalidateQueries([
    q.tprm.complexityModels.filter(),
    q.tprm.complexityModel.filter(modelId),
    q.tprm.complexityModelVersions.filter(modelId),
  ]);
  return res;
}
